import {
  clientFieldError,
  clientStepKeys,
  type ClientFormState,
} from './client-public-validation';
import { FORM_FIELD_CONSTRAINTS, type FormFieldKey } from './form-constraints';

export type TerritoryPayload = {
  id: string;
  label: string;
  active: number | boolean;
  is_test?: number | boolean;
  restored?: boolean;
};

type ApiError = Error & { details?: unknown };

async function adminApi<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const data = (response.headers.get('content-type') || '').includes('json')
    ? await response.json()
    : await response.text();
  if (!response.ok) {
    throw Object.assign(new Error((data as any)?.error || `Erreur ${response.status}`), {
      details: (data as any)?.details,
    }) as ApiError;
  }
  return data as T;
}

function escapeHtml(value: unknown): string {
  return String(value ?? '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#039;',
    '"': '&quot;',
  }[char] || char));
}

function toast(message: string, kind: 'success' | 'error' | 'info' = 'success'): void {
  let host = document.getElementById('liveToastHost');
  if (!host) {
    host = document.createElement('div');
    host.id = 'liveToastHost';
    host.className = 'live-toast-host';
    host.setAttribute('aria-live', 'polite');
    document.body.append(host);
  }
  const item = document.createElement('div');
  item.className = `live-toast live-toast-${kind}`;
  item.textContent = message;
  host.append(item);
  requestAnimationFrame(() => item.classList.add('show'));
  window.setTimeout(() => {
    item.classList.remove('show');
    window.setTimeout(() => item.remove(), 220);
  }, kind === 'error' ? 5000 : 2600);
}

function setBusy(element: HTMLButtonElement, busy: boolean, busyLabel = 'Enregistrement…'): void {
  if (busy) {
    if (!element.dataset.originalLabel) element.dataset.originalLabel = element.textContent || '';
    element.disabled = true;
    element.classList.add('is-saving');
    element.setAttribute('aria-busy', 'true');
    element.textContent = busyLabel;
    return;
  }
  element.disabled = false;
  element.classList.remove('is-saving');
  element.removeAttribute('aria-busy');
  if (element.dataset.originalLabel) {
    element.textContent = element.dataset.originalLabel;
    delete element.dataset.originalLabel;
  }
}

export function territoryRowHtml(t: TerritoryPayload): string {
  const active = Boolean(Number(t.active));
  const isTest = Boolean(Number(t.is_test));
  return `<div class="territory-edit" data-territory-id="${escapeHtml(t.id)}" data-active="${active ? '1' : '0'}" data-test="${isTest ? '1' : '0'}">
    <div>
      <strong>${escapeHtml(t.label)}</strong>
      <small>${isTest ? 'Test' : 'Réel'} · <span class="territory-state ${active ? 'is-active' : 'is-inactive'}">${active ? 'Actif' : 'Inactif'}</span></small>
    </div>
    <div class="territory-crud-actions">
      <button type="button" class="ghost small liveToggleTerritory">${active ? 'Désactiver' : 'Réactiver'}</button>
      <button type="button" class="ghost small liveEditTerritory">Modifier</button>
      <button type="button" class="danger small liveDeleteTerritory">Supprimer</button>
    </div>
  </div>`;
}

function updateTerritoryRow(row: HTMLElement, patch: Partial<TerritoryPayload>): void {
  if (patch.label !== undefined) {
    const label = row.querySelector<HTMLElement>('strong');
    if (label) label.textContent = patch.label;
  }
  if (patch.active !== undefined) {
    const active = Boolean(Number(patch.active));
    row.dataset.active = active ? '1' : '0';
    const state = row.querySelector<HTMLElement>('.territory-state');
    if (state) {
      state.textContent = active ? 'Actif' : 'Inactif';
      state.classList.toggle('is-active', active);
      state.classList.toggle('is-inactive', !active);
    }
    const toggle = row.querySelector<HTMLButtonElement>('.liveToggleTerritory');
    if (toggle) toggle.textContent = active ? 'Désactiver' : 'Réactiver';
  }
}

function markTerritoryRowSaved(row: HTMLElement): void {
  row.classList.remove('live-saved');
  void row.offsetWidth;
  row.classList.add('live-saved');
  window.setTimeout(() => row.classList.remove('live-saved'), 900);
}

function bindTerritoryRow(row: HTMLElement): void {
  if (row.dataset.liveBound === '1') return;
  row.dataset.liveBound = '1';
  const id = row.dataset.territoryId;
  if (!id) return;

  row.querySelector<HTMLButtonElement>('.liveToggleTerritory')?.addEventListener('click', async (event) => {
    event.preventDefault();
    const btn = event.currentTarget as HTMLButtonElement;
    const previous = row.dataset.active === '1';
    const next = !previous;
    updateTerritoryRow(row, { active: next ? 1 : 0 });
    setBusy(btn, true, next ? 'Activation…' : 'Désactivation…');
    try {
      const result = await adminApi<{ ok: boolean; active: boolean }>(`/admin/api/admin/territories/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: next }),
      });
      updateTerritoryRow(row, { active: result.active ? 1 : 0 });
      markTerritoryRowSaved(row);
      toast(result.active ? 'Territoire activé.' : 'Territoire désactivé.');
    } catch (error) {
      updateTerritoryRow(row, { active: previous ? 1 : 0 });
      toast(error instanceof Error ? error.message : 'Modification impossible.', 'error');
    } finally {
      setBusy(btn, false);
      btn.textContent = row.dataset.active === '1' ? 'Désactiver' : 'Réactiver';
    }
  });

  row.querySelector<HTMLButtonElement>('.liveEditTerritory')?.addEventListener('click', async (event) => {
    event.preventDefault();
    const btn = event.currentTarget as HTMLButtonElement;
    const label = row.querySelector<HTMLElement>('strong');
    const previous = label?.textContent?.trim() || '';
    const next = window.prompt('Nouveau nom du territoire', previous)?.trim();
    if (!next || next === previous) return;

    setBusy(btn, true, 'Enregistrement…');
    if (label) label.textContent = next;
    try {
      const result = await adminApi<{ ok: boolean; label: string }>(`/admin/api/admin/territories/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ label: next }),
      });
      updateTerritoryRow(row, { label: result.label || next });
      markTerritoryRowSaved(row);
      toast('Territoire renommé.');
    } catch (error) {
      if (label) label.textContent = previous;
      toast(error instanceof Error ? error.message : 'Modification impossible.', 'error');
    } finally {
      setBusy(btn, false);
    }
  });

  row.querySelector<HTMLButtonElement>('.liveDeleteTerritory')?.addEventListener('click', async (event) => {
    event.preventDefault();
    const btn = event.currentTarget as HTMLButtonElement;
    const label = row.querySelector<HTMLElement>('strong')?.textContent?.trim() || 'ce territoire';
    if (!confirm(`Supprimer « ${label} » ?\n\nIl disparaîtra immédiatement du formulaire public. Les candidatures historiques conserveront leur territoire.`)) return;

    setBusy(btn, true, 'Suppression…');
    row.classList.add('is-removing');
    try {
      await adminApi(`/admin/api/admin/territories/${encodeURIComponent(id)}`, { method: 'DELETE' });
      row.classList.add('live-remove');
      window.setTimeout(() => row.remove(), 220);
      toast('Territoire supprimé.');
    } catch (error) {
      row.classList.remove('is-removing');
      setBusy(btn, false);
      toast(error instanceof Error ? error.message : 'Suppression impossible.', 'error');
    }
  });
}

function bindAddTerritory(card: HTMLElement): void {
  const add = card.querySelector<HTMLButtonElement>('#addTerritory');
  const input = card.querySelector<HTMLInputElement>('#newTerritory');
  const list = card.querySelector<HTMLElement>('.territory-list');
  if (!add || !input || !list || add.dataset.liveBound === '1') return;
  add.dataset.liveBound = '1';

  const run = async (event?: Event) => {
    event?.preventDefault();
    const label = input.value.replace(/\s+/g, ' ').trim();
    if (label.length < 2) {
      input.focus();
      toast('Saisissez un nom de territoire valide.', 'error');
      return;
    }
    setBusy(add, true, 'Ajout…');
    try {
      const result = await adminApi<{ id: string; label: string; restored?: boolean }>('/admin/api/admin/territories', {
        method: 'POST',
        body: JSON.stringify({ label }),
      });
      input.value = '';
      const existing = list.querySelector<HTMLElement>(`[data-territory-id="${CSS.escape(result.id)}"]`);
      if (existing) {
        updateTerritoryRow(existing, { label: result.label, active: 1 });
        markTerritoryRowSaved(existing);
      } else {
        const holder = document.createElement('div');
        holder.innerHTML = territoryRowHtml({ id: result.id, label: result.label, active: 1, is_test: 0 });
        const row = holder.firstElementChild as HTMLElement;
        list.append(row);
        bindTerritoryRow(row);
        markTerritoryRowSaved(row);
      }
      toast(result.restored ? 'Territoire restauré et activé.' : 'Territoire ajouté.');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Ajout impossible.', 'error');
    } finally {
      setBusy(add, false);
      input.focus();
    }
  };

  add.addEventListener('click', run);
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') void run(event);
  });
}

function bindContractUpload(card: HTMLElement): void {
  const btn = card.querySelector<HTMLButtonElement>('#uploadContract');
  const fileInput = card.querySelector<HTMLInputElement>('#contractFile');
  if (!btn || !fileInput || btn.dataset.liveBound === '1') return;
  btn.dataset.liveBound = '1';

  btn.addEventListener('click', async (event) => {
    event.preventDefault();
    const file = fileInput.files?.[0];
    if (!file) {
      toast('Choisissez d’abord le PDF V1.8.', 'error');
      return;
    }
    setBusy(btn, true, 'Téléversement…');
    const fd = new FormData();
    fd.set('file', file);
    try {
      const result = await adminApi<{ sha256: string }>('/admin/api/admin/contract', { method: 'POST', body: fd });
      const heading = card.querySelector('h2');
      const statusParagraph = heading?.nextElementSibling as HTMLElement | null;
      if (statusParagraph) statusParagraph.innerHTML = '<span class="ok">✓ PDF configuré</span>';
      let code = card.querySelector('code');
      if (!code) {
        code = document.createElement('code');
        statusParagraph?.after(code);
      }
      code.textContent = result.sha256 || '';
      fileInput.value = '';
      card.classList.add('live-saved');
      window.setTimeout(() => card.classList.remove('live-saved'), 900);
      toast('Convention V1.8 enregistrée.');
    } catch (error) {
      const msg = card.querySelector<HTMLElement>('#contractMsg');
      if (msg) msg.innerHTML = `<div>${escapeHtml(error instanceof Error ? error.message : 'Téléversement impossible.')}</div>`;
      toast(error instanceof Error ? error.message : 'Téléversement impossible.', 'error');
    } finally {
      setBusy(btn, false);
    }
  });
}

export function enhanceTerritorySettings(): void {
  const list = document.querySelector<HTMLElement>('.territory-list');
  if (!list) return;
  const card = list.closest<HTMLElement>('.settings-card');
  if (!card) return;

  if (!card.querySelector('.territory-help')) {
    const help = document.createElement('p');
    help.className = 'territory-help muted';
    help.textContent = 'Les changements sont enregistrés immédiatement. Aucun rechargement de page n’est nécessaire.';
    list.before(help);
  }

  Array.from(list.children).forEach((child) => bindTerritoryRow(child as HTMLElement));
  bindAddTerritory(card);

  const conventionCard = document.querySelector<HTMLElement>('.settings-card:first-child');
  if (conventionCard) bindContractUpload(conventionCard);
}

const touchedFields = new Set<string>();

function liveValue(key: string): unknown {
  const byId = document.getElementById(key) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
  if (byId) {
    if (byId instanceof HTMLInputElement && byId.type === 'checkbox') return byId.checked;
    return byId.value;
  }
  const radio = document.querySelector<HTMLInputElement>(`input[name="${key}"]:checked`);
  if (!radio) return null;
  return radio.value === 'Oui';
}

function liveState(step: number): ClientFormState {
  const state: ClientFormState = {};
  const candidates = [
    'territoryId', 'companyName', 'legalForm', 'siret', 'vatNumber', 'address', 'displayName',
    'representativeName', 'phone', 'email', 'professionalPhone', 'priorityEnabled', 'joeDriverPhone',
    'circleDrivers', 'networkExpansionAllowed', 'pricePerKmTtc', 'minimumPriceTtc',
    'partnerCommissionPercent', 'minimumCommissionTtc', 'paymentProvider', 'partnerPaymentUrl', 'certification',
  ];
  candidates.forEach((key) => {
    const element = document.getElementById(key) || document.querySelector(`input[name="${key}"]`);
    if (element) state[key] = liveValue(key);
  });
  if (step === 2 && !Object.prototype.hasOwnProperty.call(state, 'priorityEnabled')) state.priorityEnabled = null;
  return state;
}

function controlsFor(key: string): Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> {
  if (key === 'priorityEnabled' || key === 'networkExpansionAllowed') {
    return Array.from(document.querySelectorAll<HTMLInputElement>(`input[name="${key}"]`));
  }
  const element = document.getElementById(key) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
  return element ? [element] : [];
}

function configureInput(key: string, element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): void {
  const requiredKeys = new Set([
    'territoryId', 'companyName', 'legalForm', 'siret', 'address', 'displayName', 'representativeName',
    'phone', 'email', 'professionalPhone', 'priorityEnabled', 'joeDriverPhone', 'networkExpansionAllowed',
    'pricePerKmTtc', 'minimumPriceTtc', 'partnerCommissionPercent', 'paymentProvider', 'partnerPaymentUrl',
    'certification',
  ]);
  if (requiredKeys.has(key)) {
    element.setAttribute('required', '');
    element.setAttribute('aria-required', 'true');
  }

  const constraint = Object.prototype.hasOwnProperty.call(FORM_FIELD_CONSTRAINTS, key)
    ? FORM_FIELD_CONSTRAINTS[key as FormFieldKey]
    : undefined;
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
    if (constraint && 'maxLength' in constraint) element.maxLength = constraint.maxLength;
    if (constraint && 'pattern' in constraint && element instanceof HTMLInputElement) element.pattern = constraint.pattern;
  }

  if (['phone', 'professionalPhone', 'joeDriverPhone'].includes(key) && element instanceof HTMLInputElement) {
    element.type = 'tel';
    element.inputMode = 'tel';
    element.autocomplete = 'tel';
    const phoneConstraint = FORM_FIELD_CONSTRAINTS[key as FormFieldKey];
    const maxDigits = 'maxDigits' in phoneConstraint ? phoneConstraint.maxDigits : 10;
    element.title = `${maxDigits} chiffres maximum (hors indicatif +33 et séparateurs).`;
    element.addEventListener('input', () => {
      const international = /^\s*\+33/iu.test(element.value);
      const digitLimit = international ? maxDigits + 1 : maxDigits;
      let digits = 0;
      element.value = Array.from(element.value).filter((character) => {
        if (!/\d/u.test(character)) return true;
        if (digits >= digitLimit) return false;
        digits += 1;
        return true;
      }).join('');
    });
  }
  if (constraint && 'inputMode' in constraint && element instanceof HTMLInputElement) {
    element.inputMode = constraint.inputMode;
  }
  if (key === 'email' && element instanceof HTMLInputElement) element.autocomplete = 'email';
  if (key === 'companyName' && element instanceof HTMLInputElement) element.autocomplete = 'organization';
  if (key === 'representativeName' && element instanceof HTMLInputElement) element.autocomplete = 'name';
  if (key === 'address' && element instanceof HTMLInputElement) element.autocomplete = 'street-address';

  if (element instanceof HTMLInputElement && ['pricePerKmTtc', 'minimumPriceTtc', 'partnerCommissionPercent', 'minimumCommissionTtc'].includes(key)) {
    element.min = '0';
    element.step = '0.01';
    const numericConstraint = FORM_FIELD_CONSTRAINTS[key as FormFieldKey];
    if (numericConstraint && 'inputMode' in numericConstraint) element.inputMode = numericConstraint.inputMode;
    if (numericConstraint && 'maxValue' in numericConstraint) element.max = String(numericConstraint.maxValue);
  }
  if (key === 'partnerPaymentUrl' && element instanceof HTMLInputElement) {
    element.removeAttribute('pattern');
    element.title = 'Le protocole http:// ou https:// est facultatif.';
    element.setAttribute('autocomplete', 'url');
  }
}

function showFieldState(key: string, step: number, force = false): boolean {
  const controls = controlsFor(key);
  if (!controls.length) return true;
  const container = controls[0]!.closest<HTMLElement>('.field, .cert');
  if (!container) return true;

  const message = clientFieldError(key, liveState(step));
  const shouldShow = force || touchedFields.has(key);
  let error = container.querySelector<HTMLElement>(`.live-field-error[data-for="${key}"]`);
  if (!error) {
    error = document.createElement('small');
    error.className = 'live-field-error';
    error.dataset.for = key;
    error.id = `live-error-${key}`;
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'polite');
    container.append(error);
  }

  error.textContent = shouldShow && message ? message : '';
  error.hidden = !(shouldShow && message);
  container.classList.toggle('live-invalid', Boolean(shouldShow && message));
  container.classList.toggle('live-valid', Boolean(shouldShow && !message));
  controls.forEach((control) => {
    control.setAttribute('aria-invalid', shouldShow && message ? 'true' : 'false');
    control.setAttribute('aria-describedby', error!.id);
  });
  return !message;
}

function refreshNextButton(step: number): void {
  const next = document.getElementById('next') as HTMLButtonElement | null;
  if (!next) return;
  const state = liveState(step);
  const valid = clientStepKeys(step, state).every((key) => clientFieldError(key, state) === null);
  next.disabled = !valid;
  next.setAttribute('aria-disabled', String(!valid));
  next.title = valid ? '' : 'Complétez correctement les champs obligatoires pour continuer.';
}

function markAndValidate(key: string, step: number): void {
  touchedFields.add(key);
  showFieldState(key, step, true);

  const dependencies: Record<string, string[]> = {
    siret: ['vatNumber'],
    minimumPriceTtc: ['minimumCommissionTtc'],
    partnerCommissionPercent: ['minimumCommissionTtc'],
    paymentProvider: ['partnerPaymentUrl'],
  };
  for (const dependency of dependencies[key] || []) {
    if (touchedFields.has(dependency)) showFieldState(dependency, step);
  }

  const legacy = document.getElementById('errs');
  if (legacy) legacy.innerHTML = '';
  refreshNextButton(step);
}

export function enhancePublicForm(step: number): void {
  const card = document.querySelector<HTMLElement>('.form-card');
  if (!card || card.dataset.liveValidationBound === '1') return;
  card.dataset.liveValidationBound = '1';

  const state = liveState(step);
  clientStepKeys(step, state).forEach((key) => {
    const controls = controlsFor(key);
    controls.forEach((control) => {
      configureInput(key, control);
      const eventName = control instanceof HTMLSelectElement ||
        (control instanceof HTMLInputElement && ['radio', 'checkbox'].includes(control.type))
        ? 'change'
        : 'input';
      control.addEventListener(eventName, () => markAndValidate(key, step));
      control.addEventListener('blur', () => markAndValidate(key, step));
    });
    if (touchedFields.has(key)) showFieldState(key, step);
  });

  refreshNextButton(step);
}

let pollTimer: number | null = null;

export function stopAdminPolling(): void {
  if (pollTimer !== null) window.clearInterval(pollTimer);
  pollTimer = null;
}

export function startAdminPolling(
  id: string,
  initialStatus: string,
  onStatusChanged: () => void,
): void {
  stopAdminPolling();
  let lastStatus = initialStatus.toUpperCase();

  pollTimer = window.setInterval(async () => {
    const currentPath = location.pathname.replace(/\/+$/, '');
    if (currentPath !== `/admin/application/${encodeURIComponent(id)}` &&
        decodeURIComponent(currentPath.replace('/admin/application/', '')) !== id) {
      stopAdminPolling();
      return;
    }
    const active = document.activeElement;
    if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement) return;
    try {
      const result = await adminApi<{ application: { status: string } }>(`/admin/api/admin/applications/${encodeURIComponent(id)}`);
      const next = String(result.application?.status || '').toUpperCase();
      if (next && lastStatus && next !== lastStatus) {
        lastStatus = next;
        toast('Le statut de la candidature a été mis à jour.', 'info');
        stopAdminPolling();
        onStatusChanged();
      } else if (next) {
        lastStatus = next;
      }
    } catch {
      // Une erreur transitoire de polling ne doit jamais perturber l’administration.
    }
  }, 8000);
}
